import React, { useContext, useState, useCallback } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { TranslatorContext, IntlContext } from '@jutro/locale';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { VehicleCoverageTable, VehicleDriverTable } from '../VehicleCoverageTable/VehicleCoverageTable';
import { EYDiscountSurchargeTable } from '../EYDiscountSurchargeTable/EYDiscountSurchargeTable';
import metadata from './PolicyVehiclesDriversInfo.metadata.json5';
import messages from './PolicyVehiclesDriversInfo.messages';
import styles from './PolicyVehiclesDriversInfo.module.scss';
import AdditionalInterestComponent from "../../AdditionalInterestComponent/AdditionalInterestComponent";
import {formatCurrency, Chevron} from '@jutro/components';


function PolicyVehiclesDriversInfo(props) {
    const translator = useContext(TranslatorContext);
    const [openItems, setOpenItems] = useState([]);
    const { value: paCoverages } = props;

    const intl = useContext(IntlContext);

    const getHeaderSection = (title) => {
        return (isOpen) => (                
            <React.Fragment>   
                <div className={styles.policyDetailsAccordion}>                 
                <Chevron isOpen={isOpen} align="right" messageProps={{label: "Accordion menuitem"}} className={styles.chevronStyle} />
                <span>{title}</span>   
                </div>                                                    
            </React.Fragment>
        );
    };

    const getFormattedDate = useCallback((item) => {
        return intl.formatDate(new Date(item), { year: 'numeric', month: 'short', day: 'numeric' });
    }, [intl]);

    const getLimitDeductibleValue = (terms, type) => {
        if (!_.isEmpty(terms) && terms[0].modelType === type) {
            return terms[0].amount;
        }
        return '-';
    };

    const getCoverage = useCallback(
        (coverages, isLineCoverage) => {
            return coverages.map((coverage) => {
                return {
                    name: coverage.name,
                    coverageType: isLineCoverage ? translator(messages.vehicleInfoPolicy) : null,
                    limit: getLimitDeductibleValue(coverage.terms, 'Limit'),
                    deductible: getLimitDeductibleValue(coverage.terms, 'Deductible'),
                    premium: coverage.premium
                };
            });
        },
        [translator]
    );

    const getDriver = useCallback(
        (drivers) => {
            return drivers.map((driver) => {
                return {
                        driverName: driver.displayName,
                        dob: getFormattedDate(new Date(
                            driver.dateOfBirth.year,
                            driver.dateOfBirth.month,
                            driver.dateOfBirth.day
                        )),
                        license: driver.licenseNumber,
                        assignment: driver.driverAssignment
                    };
              });
        },
        [getFormattedDate]
    );

    const getDiscountSurcharge = useCallback(
        (discountSurcharges) => {
            return discountSurcharges.map((discountSurcharge) => {
                return {
                    name: discountSurcharge.category
                };
            });
        },
        []
    );

    const lineCoverage = getCoverage(paCoverages.coverageDTOs, true);

    const toggleShowHide = useCallback(
        (item) => {
            const openedItem = _.clone(openItems);
            const index = _.indexOf(openedItem, item);
            if (index === -1) {
                openedItem.push(item);
                setOpenItems(openedItem);
            } else {
                setOpenItems(_.slice(openedItem, index, 0));
            }
        },
        [openItems]
    );

    const getVehicleTitle = (vehicleDTO) => {
        const vehiclePremium = formatCurrency(intl, { value: vehicleDTO.premium.amount, currency: 'cad', showFractions: true, currencyDisplay: 'symbol' })
        const vehicleDisplayName = `${vehicleDTO.year} ${vehicleDTO.make} ${vehicleDTO.model}`
        return vehicleDisplayName
    }

    function getCell(items, index, property) {
        return !_.isObject(items[property.id]) ? items[property.id] : null;
    }

    const generateVehicleOverrides = useCallback(() => {
     //   console.log('pavehicleDriver: ', paCoverages);
        const overrides = paCoverages.vehicleDTOs.map((vehicleDTO, index) => {
      //        console.log('vehicleDTO: ', vehicleDTO);
            const vehicleCoverage = getCoverage(vehicleDTO.coverages);
            const vehicleDriver = !_.isEmpty(vehicleDTO.drivers) ? getDriver(vehicleDTO.drivers) : [];
          //  const vehicleDiscountSurcharge = !_.isEmpty(vehicleDTO.ey_DiscountSurcharges) ? getDiscountSurcharge(vehicleDTO.ey_DiscountSurcharges) : [];
            const vehicleDiscountSurcharge = !_.isEmpty(vehicleDTO.ey_Discounts) ? getDiscountSurcharge(vehicleDTO.ey_Discounts) : [];
            return {
                [`driverInfo${index}`]: {
                    data: vehicleDriver,
                    show: openItems
                },
                [`vehicleCoverageInfo${index}`]: {
                    data: _.concat(lineCoverage, vehicleCoverage),
                    show: openItems
                },
                [`additionalInterestTable${index}`] : {
                    data: vehicleDTO.additionalInterests,
                    visible: !_.isEmpty(vehicleDTO.additionalInterests),
                    show: openItems
                },
                [`vehicleDiscountSurchargeInfo${index}`]: {
                    data: vehicleDiscountSurcharge,
                    show: openItems
                },
                [`showContentToggleLink${index}`]: {
                    onClick: () => toggleShowHide(index)
                },
                [`contentDetailToggle${index}`]: {
                    icon: _.includes(openItems, index) ? 'caret-up' : 'caret-down',
                    message: _.includes(openItems, index)
                        ? translator(messages.hideVehicleCoverages)
                        : translator(messages.showVehicleCoverages),
                    className: "linkBackground"
                },
                [`vehicleCoverageContainer${index}`]: {
                    visible: _.includes(openItems, index)
                },
                [`driverAssignmentInfo${index}`]: {
                    data: vehicleDTO.drivers
                },
                [`driverAssignmentSection${index}`]: {
                    visible: vehicleDTO.drivers?.length > 0
                },
            };
        });

        return Object.assign({}, ...overrides);
        // eslint-disable-next-line max-len
    }, [getCoverage, getDiscountSurcharge, getDriver, lineCoverage, openItems, paCoverages.vehicleDTOs, toggleShowHide, translator]);

    const generateDriverOverrides = useCallback(() => {
        const overrides = paCoverages.driverDTOs.map((driverDTO, index) => {
            return {
                [`driverDOB${index}`]: {
                    value: getFormattedDate(new Date(
                        driverDTO.dateOfBirth.year,
                        driverDTO.dateOfBirth.month,
                        driverDTO.dateOfBirth.day
                    ))
                }
            };
        });

        return Object.assign({}, ...overrides);
    }, [getFormattedDate, paCoverages.driverDTOs]);

    const generateVehicleTitleOverrides = useCallback(() => {
        const overrides = paCoverages.vehicleDTOs.map((vehicleDTO, index) => {
            return {
                [`vehicleMake${index}`]: {
                    content: `${vehicleDTO.year} ${vehicleDTO.make} ${vehicleDTO.model} - VIN# ${vehicleDTO.vin}`
                },
                // [`vehicleTitle${index}`]: {
                //      content: getVehicleTitle(vehicleDTO)
                // },
                [`vehicleAccordionCard${index}`]:{
                    header: getHeaderSection(getVehicleTitle(vehicleDTO)) 
                }
            };
        });

        return Object.assign({}, ...overrides);
    }, [paCoverages]);

    const overrides = {
        ...generateDriverOverrides(),
        ...generateVehicleOverrides(),
        ...generateVehicleTitleOverrides()
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            vehiclecoverage: VehicleCoverageTable,
            vehicledriver: VehicleDriverTable,
            vehiclediscountsurcharge: EYDiscountSurchargeTable,
            additionalinterestcomponent: AdditionalInterestComponent
        },
        resolveCallbackMap: {
            getCellCoverage: getCell
        },
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={paCoverages}
            overrideProps={overrides}
            classNameMap={resolvers.resolveClassNameMap}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
        />
    );
}

PolicyVehiclesDriversInfo.propTypes = {
    value: PropTypes.shape({
        coverageDTOs: PropTypes.arrayOf(PropTypes.shape({})),
        vehicleDTOs: PropTypes.arrayOf(PropTypes.shape({})),
        driverDTOs: PropTypes.arrayOf(PropTypes.shape({})),
        additionalLiabilityCoverages: PropTypes.arrayOf(PropTypes.shape({})),
        additionalPropertyCoverages: PropTypes.arrayOf(PropTypes.shape({}))
    }).isRequired
};

export default PolicyVehiclesDriversInfo;
